import React, { useState, useEffect } from 'react';
import { Settings, ToggleLeft, ToggleRight, Save, Loader2 } from 'lucide-react';
import { supabase } from '../../services/supabase';

const AdminSettingsStyles = () => (
  <style>{`
    .settings-page {
      max-width: 640px;
    }
    .settings-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 28px;
    }
    .settings-header h1 {
      font-size: 1.4rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }
    .settings-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 28px;
    }
    .settings-card-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 8px;
    }
    .settings-card-desc {
      font-size: 0.88rem;
      color: #64748b;
      margin-bottom: 24px;
      line-height: 1.5;
    }
    .settings-field {
      margin-bottom: 24px;
    }
    .settings-field:last-child {
      margin-bottom: 0;
    }
    .settings-label {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
      color: #334155;
      margin-bottom: 8px;
    }
    .settings-input {
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 0.95rem;
      font-family: inherit;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }
    .settings-input:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(47,125,94,0.12);
    }
    .settings-input:disabled {
      background: #f8fafc;
      color: #94a3b8;
    }
    .settings-toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0;
      border-bottom: 1px solid #f1f5f9;
    }
    .settings-toggle-row:last-child {
      border-bottom: none;
    }
    .settings-toggle-info {
      flex: 1;
    }
    .settings-toggle-label {
      font-size: 0.95rem;
      font-weight: 500;
      color: #1e293b;
    }
    .settings-toggle-desc {
      font-size: 0.82rem;
      color: #64748b;
      margin-top: 2px;
    }
    .settings-toggle-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-left: 16px;
    }
    .settings-preview {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px;
      margin-top: 24px;
    }
    .settings-preview-title {
      font-size: 0.85rem;
      font-weight: 600;
      color: #64748b;
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .settings-preview-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      font-size: 0.92rem;
    }
    .settings-preview-original {
      text-decoration: line-through;
      color: #94a3b8;
    }
    .settings-preview-discounted {
      color: #e53e3e;
      font-weight: 600;
    }
    .settings-save-bar {
      margin-top: 24px;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .settings-save-btn {
      padding: 12px 32px;
      border: none;
      border-radius: 8px;
      background: #2F7D5E;
      color: #fff;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background 0.2s;
      font-family: inherit;
    }
    .settings-save-btn:hover {
      background: #245F48;
    }
    .settings-save-btn:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
    .settings-save-msg {
      font-size: 0.88rem;
      color: #16a34a;
      font-weight: 500;
    }
    .settings-rate-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .settings-rate-input {
      width: 100px;
      padding: 12px 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 1.1rem;
      font-weight: 600;
      text-align: center;
      font-family: inherit;
      transition: border-color 0.2s;
    }
    .settings-rate-input:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(47,125,94,0.12);
    }
    .settings-rate-input:disabled {
      background: #f8fafc;
      color: #94a3b8;
    }
    .settings-rate-suffix {
      font-size: 1rem;
      color: #64748b;
      font-weight: 500;
    }
    .settings-rate-hint {
      font-size: 0.82rem;
      color: #94a3b8;
      margin-top: 6px;
    }
  `}</style>
);

const AdminSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [rateInput, setRateInput] = useState('88');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from('restaurant_settings')
          .select('value')
          .eq('key', 'global_discount')
          .single();
        if (data?.value) {
          setEnabled(data.value.enabled || false);
          setRateInput(String(Math.round((data.value.rate || 0.88) * 100)));
          setLabel(data.value.label || '');
        }
      } catch (e) {
        // No existing setting — use defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const rate = Number(rateInput) / 100;
  const isValidRate = !isNaN(rate) && rate > 0 && rate < 1;

  const handleSave = async () => {
    const saveRate = isValidRate ? rate : 0.88;
    if (enabled && !isValidRate) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const value = {
        enabled,
        rate: saveRate,
        label: label.trim() || `全單${isValidRate ? rateInput : '88'}折`,
      };

      const { error } = await supabase
        .from('restaurant_settings')
        .upsert({ key: 'global_discount', value }, { onConflict: 'key' });

      if (error) throw error;
      setSaveMsg('已儲存');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      alert('儲存失敗：' + (e.message || '未知錯誤'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <AdminSettingsStyles />
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p>載入中...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminSettingsStyles />
      <div className="settings-page">
        <div className="settings-header">
          <Settings size={24} color="#2F7D5E" />
          <h1>優惠設定</h1>
        </div>

        <div className="settings-card">
          <div className="settings-card-title">全單折扣</div>
          <div className="settings-card-desc">
            開啟後，單點模式的所有品項（含配料加價）將按折扣率計算，稅額也按折後金額計算。週訂模式不受影響。
          </div>

          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <div className="settings-toggle-label">啟用全單折扣</div>
              <div className="settings-toggle-desc">
                {enabled ? '折扣已開啟，前台菜單將顯示折後價' : '折扣已關閉'}
              </div>
            </div>
            <button className="settings-toggle-btn" onClick={() => setEnabled(!enabled)}>
              {enabled ? (
                <ToggleRight size={40} color="#2F7D5E" />
              ) : (
                <ToggleLeft size={40} color="#cbd5e1" />
              )}
            </button>
          </div>

          <div className="settings-field" style={{ marginTop: 20 }}>
            <label className="settings-label">折扣率</label>
            <div className="settings-rate-row">
              <input
                className="settings-rate-input"
                type="number"
                min="1"
                max="99"
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                disabled={!enabled}
              />
              <span className="settings-rate-suffix">折</span>
            </div>
            <div className="settings-rate-hint">
              輸入 88 = 88折（即 12% off），輸入 75 = 75折（即 25% off）
            </div>
            {rateInput && !isValidRate && (
              <div style={{ color: '#e53e3e', fontSize: '0.82rem', marginTop: 4 }}>
                請輸入 1~99 之間的數字
              </div>
            )}
          </div>

          <div className="settings-field">
            <label className="settings-label">顯示標籤（選填）</label>
            <input
              className="settings-input"
              type="text"
              placeholder={`全單${rateInput}折`}
              value={label}
              onChange={e => setLabel(e.target.value)}
              disabled={!enabled}
              maxLength={30}
            />
          </div>

          {enabled && isValidRate && (
            <div className="settings-preview">
              <div className="settings-preview-title">預覽效果</div>
              <div className="settings-preview-item">
                <span>原價 $10.00 的品項</span>
                <span>
                  <span className="settings-preview-original">$10.00</span>
                  {' '}
                  <span className="settings-preview-discounted">${(10 * rate).toFixed(2)}</span>
                </span>
              </div>
              <div className="settings-preview-item">
                <span>原價 $15.50 的品項</span>
                <span>
                  <span className="settings-preview-original">$15.50</span>
                  {' '}
                  <span className="settings-preview-discounted">${(15.5 * rate).toFixed(2)}</span>
                </span>
              </div>
              <div className="settings-preview-item" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>標籤顯示</span>
                <span style={{
                  background: 'linear-gradient(90deg, #e53e3e, #c53030)',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: 4,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                }}>
                  {label.trim() || `全單${rateInput}折`}
                </span>
              </div>
            </div>
          )}

          <div className="settings-save-bar">
            <button
              className="settings-save-btn"
              onClick={handleSave}
              disabled={saving || (enabled && !isValidRate)}
            >
              {saving ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
              {saving ? '儲存中...' : '儲存設定'}
            </button>
            {saveMsg && <span className="settings-save-msg">{saveMsg}</span>}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSettings;
